import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    db,
    collection,
    getDocs,
    writeBatch,
    doc,
    setDoc,
    query,
    orderBy,
    limit,
    getDoc,
    addDoc,
    deleteDoc,
    updateDoc,
} from "../firebase";
import { getAuth, signInAnonymously } from "firebase/auth";
import { toast } from "react-toastify";

/* ---------- helpers ------------ */
const normalize = (s = "") =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();

const toDate = (v) => {
    try {
        if (typeof v === "number") return new Date(v);
        if (v?.toDate) return v.toDate();
        if (typeof v === "string") return new Date(v);
    } catch (_) { }
    return null;
};

const fmtDateTime = (ts) => {
    const d = toDate(ts);
    if (!d || Number.isNaN(d.getTime())) return "-";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

const safeArray = (x) => (Array.isArray(x) ? x : []);
const safeStr = (x) => (typeof x === "string" ? x : "");
const safeNum = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);

const statusToEs = (s) => {
    const k = String(s || "").toLowerCase();
    switch (k) {
        case "pending":
            return "Pendiente";
        case "running":
            return "En proceso";
        // histórico "done" y nuevo "enviado" → "Enviado"
        case "done":
        case "enviado":
            return "Enviado";
        case "entregado":
            return "Entregado";
        case "error":
            return "Error";
        default:
            return "-";
    }
};

function isGroupsRun(doc) {
  const s = String(doc?.source || "");
  return s === "reposicion_groups" || s === "reposicionGroups" || s === "groups";
}


const statusBadgeClass = (s) => {
    const k = String(s || "").toLowerCase();
    if (k === "entregado") return "badge-success";
    if (k === "enviado" || k === "done") return "badge-info";
    if (k === "running") return "badge-warning";
    if (k === "error") return "badge-error";
    if (k === "pending") return "badge-ghost";
    return "badge-ghost";
};


const fmtYMD = (ymd) => {
    const s = String(ymd || "");
    if (s.length !== 8) return s || "-";
    return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
};

const normText = (s = "") =>
    String(s || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
        .trim();

const uniqKeywords = (arr = []) => {
    const out = [];
    const seen = new Set();
    for (const k of arr) {
        const kk = String(k || "").trim();
        if (!kk) continue;
        const key = normText(kk);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(kk);
    }
    return out;
};


const uniqSkus = (arr = []) => {
    const out = [];
    const seen = new Set();

    for (const x of arr) {
        const sku = String(x || "").trim();
        if (!sku) continue;

        const key = sku.toUpperCase();
        if (seen.has(key)) continue;

        seen.add(key);
        out.push(sku);
    }

    return out;
};


          // ✅ URL del endpoint manual de grupos (Cloud Run / Function HTTP)
const GROUPS_RUN_URL = "https://reposiciongroupsmanualrun-ieitjwau4a-uc.a.run.app";
// Nueva función HTTP; no altera la reposición tradicional
const ROTACION_SEND_URL = "https://enviarrotacionmanualconsolidada-ieitjwau4a-uc.a.run.app";
const ROTACION_DOWNLOAD_URL = "https://descargarrotacionmanualconsolidada-ieitjwau4a-uc.a.run.app";

// ✅ Token (Vite): ponelo en .env como VITE_REPO_GROUPS_TOKEN="..."
const GROUPS_RUN_TOKEN ="repo_manual_2026_x3RkP9mL2sAqZ8";




/* ---- helpers automatización ---- */
const TZ = "America/Argentina/Buenos_Aires";

function todayYMD_BA() {
    const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: TZ,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
    return fmt.format(new Date()); // YYYY-MM-DD
}

function daysBetweenUTC(baseISO, target) {
    if (!baseISO) return NaN;
    const b = new Date(baseISO);
    const d0 = new Date(Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate()));
    const d = new Date(Date.UTC(target.getFullYear(), target.getMonth(), target.getDate()));
    const diffMs = d.getTime() - d0.getTime();
    return Math.floor(diffMs / 86400000);
}

function isDueToday(startISO, everyDays) {
    if (!startISO || !everyDays || everyDays <= 0) return false;
    const today = new Date(todayYMD_BA() + "T00:00:00-03:00");
    const delta = daysBetweenUTC(startISO, today);
    return delta >= 0 && delta % everyDays === 0;
}

function nextRunISO(startISO, everyDays) {
    if (!startISO || !everyDays || everyDays <= 0) return null;
    const start = new Date(startISO + "T00:00:00-03:00");
    const today = new Date(todayYMD_BA() + "T00:00:00-03:00");
    if (start >= today) return start.toISOString().slice(0, 10);
    const deltaDays = Math.floor((today.getTime() - start.getTime()) / 86400000);
    const k = Math.ceil(deltaDays / everyDays);
    const next = new Date(start.getTime() + k * everyDays * 86400000);
    return next.toISOString().slice(0, 10);
}




function dueAndNextFromSchedule(sched) {
  if (!hasValidScheduleObj(sched)) return { due: false, nextISO: null };
  const startISO = normalizeStartYMD(sched.startISO);
  const everyDays = Number(sched.everyDays);
  return {
    due: isDueToday(startISO, everyDays),
    nextISO: nextRunISO(startISO, everyDays),
  };
}





function isValidYMD(ymd) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(ymd || "").trim());
}

function normalizeStartYMD(x) {
  const s = String(x || "").trim();
  const ymd = s.slice(0, 10);
  return isValidYMD(ymd) ? ymd : "";
}

function hasValidScheduleObj(s) {
  const startISO = normalizeStartYMD(s?.startISO);
  const everyDays = Number(s?.everyDays || 0);
  return !!s?.enabled && !!startISO && everyDays > 0;
}

function computeDueBadgeForLab(lab) {
  const today = todayYMD_BA(); // "YYYY-MM-DD"
  const sucIds = ["1", "2", "3"];

  // 1) juntar schedules individuales "bien configurados"
  const validIndividuals = [];
  for (const sid of sucIds) {
    const sc = lab?.sucursales?.[sid] || {};
    const schSuc = sc?.schedule || {};
    if (sc.enabled === false) continue; // sucursal deshabilitada => ignorar

    if (hasValidScheduleObj(schSuc)) {
      validIndividuals.push({
        sid,
        startISO: normalizeStartYMD(schSuc.startISO),
        everyDays: Number(schSuc.everyDays),
      });
    }
  }

  // helper: next run (devuelve YYYY-MM-DD o null)
  const nextRunYMD = (startISO, everyDays) => {
    const s0 = normalizeStartYMD(startISO);
    const ed = Number(everyDays || 0);
    if (!s0 || !ed || ed <= 0) return null;
    return nextRunISO(s0, ed); // usa tu función existente
  };

  // 2) si hay individuales válidos => badge sale de ahí
  if (validIndividuals.length) {
    const dueToday = validIndividuals.some((x) => isDueToday(x.startISO, x.everyDays));

    const nextDates = validIndividuals
      .map((x) => nextRunYMD(x.startISO, x.everyDays))
      .filter(Boolean)
      .sort(); // YYYY-MM-DD ordena lexicográfico ok

    const nextISO = nextDates.length ? nextDates[0] : null;

    return {
      mode: "individual",
      dueToday,
      nextISO,
      // opcional para debug/UI:
      // nextFromSuc: nextDates.length ? validIndividuals.find(v => nextRunYMD(v.startISO, v.everyDays) === nextISO)?.sid : null
    };
  }

  // 3) fallback al GENERAL
  const gen = lab?.schedule || {};
  if (hasValidScheduleObj(gen)) {
    const dueToday = isDueToday(normalizeStartYMD(gen.startISO), Number(gen.everyDays));
    const nextISO = nextRunYMD(gen.startISO, gen.everyDays);
    return { mode: "general", dueToday, nextISO };
  }

  // 4) sin programación
  return { mode: "none", dueToday: false, nextISO: null };
}
/**
 * Replica la lógica del backend para la UI:
 * - si existe al menos un schedule individual válido -> manda por individuales
 *   (si ownerSuc existe, solo considera esa sucursal)
 * - si no hay ningún individual válido -> usa general
 *
 * Devuelve:
 * { dueToday, nextISO, mode, dueSucIds }
 */
// function computeDueBadgeForLab(lab) {
//   const gen = lab?.schedule || {};
//   const suc = lab?.sucursales || {};
//   const ownerSuc = String(lab?.ownerSuc || "").trim(); // "1"|"2"|"3"|""


//   // 1) recolectar sucursales habilitadas con schedule individual válido
//   const sucIds = ["1", "2", "3"].filter((sid) => (suc?.[sid]?.enabled !== false));

//   const validIndividualSucIds = sucIds.filter((sid) => {
//     const sch = suc?.[sid]?.schedule || {};
//     return hasValidScheduleObj(sch);
//   });

//   const hasAnyValidIndividual = validIndividualSucIds.length > 0;

//   // Si hay ownerSuc, la corrida unificada evalúa SOLO ese schedule efectivo
//   const effectiveSucIds = ownerSuc ? [ownerSuc] : sucIds;

//   // 2) Si hay algún individual válido, usamos individuales (no general)
//   if (hasAnyValidIndividual) {
//     let dueToday = false;
//     let nextISO = null;
//     const dueSucIds = [];

//     for (const sid of effectiveSucIds) {
//       const sch = suc?.[sid]?.schedule || {};
//       if (!hasValidScheduleObj(sch)) continue;

//       const r = dueAndNextFromSchedule(sch);
//       if (r.due) {
//         dueToday = true;
//         dueSucIds.push(sid);
//       }
//       // próxima = mínimo entre las próximas de las sucursales válidas
//       if (r.nextISO) {
//         if (!nextISO || r.nextISO < nextISO) nextISO = r.nextISO;
//       }
//     }

//     return {
//       dueToday,
//       nextISO,
//       mode: ownerSuc ? `individual_owner_${ownerSuc}` : "individual",
//       dueSucIds,
//     };
//   }

//   // 3) Si NO hay individuales válidos, usamos general (fallback)
//   const r = dueAndNextFromSchedule(gen);
//   return {
//     dueToday: r.due,
//     nextISO: r.nextISO,
//     mode: "general",
//     dueSucIds: [],
//   };
// }



/* diferencia de días entre createdAt y ahora (Buenos Aires) */
function diffDaysFromNow(ts) {
    const d = toDate(ts);
    if (!d) return null;
    // normalizamos ambas fechas a YYYY-MM-DD en -03:00 para que no joda la hora
    const nowBA = new Date(
        new Date().toLocaleString("en-US", { timeZone: TZ })
    );
    const startDay = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const nowDay = new Date(Date.UTC(nowBA.getFullYear(), nowBA.getMonth(), nowBA.getDate()));
    const diffMs = nowDay.getTime() - startDay.getTime();
    return Math.floor(diffMs / 86400000);
}

/* devuelve un <span> con badge según la edad */
function ageBadge(h) {
    const dd = diffDaysFromNow(h?.createdAt);
    if (dd === null) return null;

    // rojo si 5 o más días
    if (dd >= 5) {
        return (
            <span className="badge badge-error ml-2 text-xs whitespace-nowrap">
                +5 días
            </span>
        );
    }

    // naranja si 2 o más días
    if (dd >= 2) {
        return (
            <span className="badge badge-warning ml-2 text-xs whitespace-nowrap">
                +2 días
            </span>
        );
    }

    // menos de 2 días -> nada
    return null;
}


/* ========== COMPONENTE ========== */
export default function ReposicionScreen() {
    const [labsDocs, setLabsDocs] = useState([]);
    const [labsSelected, setLabsSelected] = useState(new Set());
    const [rotacionLoading, setRotacionLoading] = useState(false);
    const [rotacionFiles, setRotacionFiles] = useState([]);
    const [rotacionEnvioResult, setRotacionEnvioResult] = useState(null);
    const [consolidadoSchedule, setConsolidadoSchedule] = useState({
      enabled: false, startISO: "", everyDays: 7, labs: [], sucursales: ["1","2","3"]
    });
    const [consolidadoScheduleLoaded, setConsolidadoScheduleLoaded] = useState(false);
    const [consolidadoScheduleSaving, setConsolidadoScheduleSaving] = useState(false);

    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);

    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [detailOpenId, setDetailOpenId] = useState(null);
    const [detailDoc, setDetailDoc] = useState(null);

    const [savingLabId, setSavingLabId] = useState(null);

    const [selectedSucursales, setSelectedSucursales] = useState(
        () => new Set(["1", "2", "3"]) // por defecto las 3
    );

    // ====== grupos de productos ======
    const [groups, setGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);

    const [groupsSelected, setGroupsSelected] = useState(new Set());
const [groupsRunning, setGroupsRunning] = useState(false);
const [groupRunId, setGroupRunId] = useState(null); // opcional: spinner por fila


    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [groupSaving, setGroupSaving] = useState(false);
    const [groupDeletingId, setGroupDeletingId] = useState(null);

    const [filterDueTodayOnly, setFilterDueTodayOnly] = useState(false);

    const [excludedSkus, setExcludedSkus] = useState([]);
const [excludedSkuInput, setExcludedSkuInput] = useState("");
const [excludedLoading, setExcludedLoading] = useState(false);

    

const emptyGroupForm = {
  id: null,
  display: "",
  active: true,
  keywords: [],
  keywordInput: "",

  skus: [],
  skuInput: "",

  match: {
    field: "descripcionDisplay",
    mode: "contains_any",
    skuMode: "exact_any",
    combine: "any",
  },

  // ✅ NUEVO
  windowDays: 7,

  schedule: {
    enabled: false,
    startISO: "",
    everyDays: "",
    timezone: TZ,
  },

  config: {
    ownerSuc: "",
    sucursales: {
      "1": { enabled: true, schedule: { enabled: false, startISO: "", everyDays: "" }, windowDays: "" },
      "2": { enabled: true, schedule: { enabled: false, startISO: "", everyDays: "" }, windowDays: "" },
      "3": { enabled: true, schedule: { enabled: false, startISO: "", everyDays: "" }, windowDays: "" },
    },
  },
};




    const [groupForm, setGroupForm] = useState(emptyGroupForm);




    const loadExcludedSkus = async () => {
  try {
    setExcludedLoading(true);
    const snap = await getDocs(collection(db, "reposicion_excluded_skus"));
    const rows = snap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((x) => x.active !== false)
      .sort((a, b) => String(a.sku || a.id).localeCompare(String(b.sku || b.id), "es", { numeric: true }));

    setExcludedSkus(rows);
  } catch (e) {
    console.error(e);
    toast.error("Error al cargar SKUs excluidos");
  } finally {
    setExcludedLoading(false);
  }
};

useEffect(() => {
  loadExcludedSkus();
}, []);

const addExcludedSku = async () => {
  const sku = String(excludedSkuInput || "").trim();
  if (!sku) return;

  const id = sku.toUpperCase();

  await setDoc(doc(db, "reposicion_excluded_skus", id), {
    sku,
    active: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }, { merge: true });

  setExcludedSkuInput("");
  toast.success("SKU excluido ✅");
  await loadExcludedSkus();
};

const removeExcludedSku = async (sku) => {
  const id = String(sku || "").trim().toUpperCase();
  if (!id) return;

  await setDoc(doc(db, "reposicion_excluded_skus", id), {
    active: false,
    updatedAt: Date.now(),
  }, { merge: true });

  toast.success("SKU habilitado nuevamente");
  await loadExcludedSkus();
};

    const loadGroups = async () => {
        try {
            setGroupsLoading(true);
            const snap = await getDocs(collection(db, "reposicion_groups"));
            const rows = snap.docs
                .map((d) => ({ id: d.id, ...(d.data() || {}) }))
                .sort((a, b) => String(a.display || "").localeCompare(String(b.display || ""), "es", { sensitivity: "base" }));
            setGroups(rows);
        } catch (e) {
            console.error(e);
            toast.error("Error al cargar grupos");
        } finally {
            setGroupsLoading(false);
        }
    };

    useEffect(() => {
        loadGroups();
    }, []);

    const openCreateGroup = () => {
        setGroupForm({ ...emptyGroupForm, id: null });
        setGroupModalOpen(true);
    };

    const openEditGroup = (g) => {
        // normalizamos estructura para evitar undefined
        const cfg = g?.config || {};
        const suc = cfg?.sucursales || {};
    const normSuc = (sid) => {
  const sc = suc?.[sid] || {};
  const sch = sc?.schedule || {};
return {
  enabled: sc.enabled !== false,
  schedule: {
    enabled: !!sch.enabled,
    startISO: sch.startISO || "",
    everyDays: sch.everyDays ? Number(sch.everyDays) : "",
  },
  windowDays: Number.isFinite(Number(sc.windowDays)) ? Number(sc.windowDays) : "",
};

};


        setGroupForm({
            ...emptyGroupForm,
            id: g.id,
            display: g.display || g.id,
            active: g.active !== false,
            keywords: Array.isArray(g.keywords) ? g.keywords : [],
            keywordInput: "",

            skus: Array.isArray(g.skus) ? g.skus : [],
            skuInput: "",

            match: {
                field: g?.match?.field || "descripcionDisplay",
                mode: g?.match?.mode || "contains_any",
                skuMode: g?.match?.skuMode || "exact_any",
                combine: g?.match?.combine || "any",
            },

              // ✅ NUEVO
windowDays: Number.isFinite(Number(g.windowDays)) ? Number(g.windowDays) : 7,


            schedule: {
                enabled: !!g?.schedule?.enabled,
                startISO: g?.schedule?.startISO || "",
                everyDays: g?.schedule?.everyDays ? Number(g.schedule.everyDays) : "",
                timezone: g?.schedule?.timezone || TZ,
            },
            config: {
                ownerSuc: cfg?.ownerSuc || "",
                sucursales: {
                    "1": normSuc("1"),
                    "2": normSuc("2"),
                    "3": normSuc("3"),
                },
            },
        });
        setGroupModalOpen(true);
    };

    const closeGroupModal = () => {
        setGroupModalOpen(false);
        setGroupForm(emptyGroupForm);
    };

    const addKeywordFromInput = () => {
        const raw = String(groupForm.keywordInput || "").trim();
        if (!raw) return;
        const next = uniqKeywords([...(groupForm.keywords || []), raw]);
        setGroupForm((p) => ({ ...p, keywords: next, keywordInput: "" }));
    };

    const removeKeyword = (kw) => {
        setGroupForm((p) => ({
            ...p,
            keywords: (p.keywords || []).filter((x) => x !== kw),
        }));
    };

    const addSkuFromInput = () => {
        const raw = String(groupForm.skuInput || "").trim();
        if (!raw) return;

        const parts = raw
            .split(/[,;\n]/)
            .map((x) => x.trim())
            .filter(Boolean);

        const next = uniqSkus([...(groupForm.skus || []), ...parts]);

        setGroupForm((p) => ({
            ...p,
            skus: next,
            skuInput: "",
        }));
    };

    const removeSku = (sku) => {
        setGroupForm((p) => ({
            ...p,
            skus: (p.skus || []).filter((x) => x !== sku),
        }));
    };

    const saveGroup = async () => {
        try {
            const display = String(groupForm.display || "").trim();
            if (!display) {
                toast.warn("Poné un nombre al grupo");
                return;
            }
            const keywords = uniqKeywords(groupForm.keywords || []);
            const skus = uniqSkus(groupForm.skus || []);

            if (!keywords.length && !skus.length) {
                toast.warn("Cargá al menos 1 palabra clave o 1 SKU");
                return;
            }

            setGroupSaving(true);

            const payload = {
                display,
                active: groupForm.active === true,
                keywords,
                skus,
                match: {
                    field: groupForm.match?.field || "descripcionDisplay",
                    mode: "contains_any",
                    skuMode: "exact_any",
                    combine: groupForm.match?.combine || "any",
                },

            windowDays: Number(groupForm.windowDays) > 0 ? Number(groupForm.windowDays) : null,



                schedule: {
                    enabled: !!groupForm.schedule?.enabled,
                    startISO: groupForm.schedule?.startISO || null,
                    everyDays: groupForm.schedule?.everyDays ? Number(groupForm.schedule.everyDays) : null,
                    timezone: groupForm.schedule?.timezone || TZ,
                },
                config: {
                    ownerSuc: groupForm.config?.ownerSuc || null,
                    sucursales: Object.fromEntries(
                        ["1", "2", "3"].map((sid) => {
                            const sc = groupForm.config?.sucursales?.[sid] || {};
                            const sch = sc.schedule || {};
                          return [
  sid,
  {
    enabled: sc.enabled !== false,
    schedule: {
      enabled: !!sch.enabled,
      startISO: sch.startISO || null,
      everyDays: sch.everyDays ? Number(sch.everyDays) : null,
    },
    windowDays: Number(sc.windowDays) > 0 ? Number(sc.windowDays) : null,
  },
];


                        })
                    ),
                },
                updatedAt: Date.now(),
                ...(groupForm.id ? {} : { createdAt: Date.now() }),
            };

            if (groupForm.id) {
                await setDoc(doc(db, "reposicion_groups", groupForm.id), payload, { merge: true });
            } else {
                await addDoc(collection(db, "reposicion_groups"), payload);
            }

            toast.success("Grupo guardado ✅");
            await loadGroups();
            closeGroupModal();
        } catch (e) {
            console.error(e);
            toast.error("No se pudo guardar el grupo");
        } finally {
            setGroupSaving(false);
        }
    };

    const deleteGroup = async (g) => {
        try {
            if (!g?.id) return;
            setGroupDeletingId(g.id);
            await deleteDoc(doc(db, "reposicion_groups", g.id));
            toast.success("Grupo eliminado 🗑️");
            await loadGroups();
        } catch (e) {
            console.error(e);
            toast.error("No se pudo eliminar el grupo");
        } finally {
            setGroupDeletingId(null);
        }
    };


    const toggleSucursalGlobal = (sucId) => {
        setSelectedSucursales((prev) => {
            const next = new Set(prev);
            if (next.has(sucId)) next.delete(sucId);
            else next.add(sucId);
            return next;
        });
    };


    useEffect(() => {
        const auth = getAuth();
        signInAnonymously(auth).catch(console.error);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const snap = await getDocs(collection(db, "laboratorios_envio"));
                const docs = snap.docs
                    .map((d) => {
                        const data = d.data() || {};
                        const cfg = data.config || {};
                        const diasFromDbRaw = cfg?.diasVentana;
                        const diasResolved = Number.isFinite(diasFromDbRaw)
                            ? Number(diasFromDbRaw)
                            : 30;

                        const ownerSuc = cfg.ownerSuc || ""; // "1" | "2" | "3" | ""

                        const sucursalesCfg = cfg.sucursales || {};
                        const sucIds = ["1", "2", "3"];

                        const sucursales = {};
                        sucIds.forEach((sid) => {
                            const sc = sucursalesCfg[sid] || {};
                            const sched = sc.schedule || {};
                            sucursales[sid] = {
                                enabled: sc.enabled !== false, // default true
                                schedule: {
                                    enabled: !!sched.enabled,
                                    startISO: sched.startISO || "",
                                    everyDays: sched.everyDays
                                        ? Number(sched.everyDays)
                                        : "",
                                },
                            };
                        });

                        return {
                            id: d.id,
                            // Clave de búsqueda (lo que se ve + fallback al id)
                            searchKey: normalize(data.display || d.id),
                            display: data.display || d.id,
                            active: data.active === true,

                            diasVentana: diasResolved,
                            diasVentanaOriginal: diasResolved,

                            ownerSuc,
                            ownerSucOriginal: ownerSuc,          // 👈 NUEVO

                            sucursales,
                            sucursalesOriginal: sucursales,      // 👈 NUEVO

                            schedule: {
                                enabled: !!data?.schedule?.enabled,
                                enabledOriginal: !!data?.schedule?.enabled,
                                startISO: data?.schedule?.startISO || "",
                                startISOOriginal: data?.schedule?.startISO || "",
                                everyDays: data?.schedule?.everyDays
                                    ? Number(data.schedule.everyDays)
                                    : null,
                                everyDaysOriginal: data?.schedule?.everyDays
                                    ? Number(data.schedule.everyDays)
                                    : null,
                                timezone: data?.schedule?.timezone || TZ,
                                lastRunAt: data?.schedule?.lastRunAt,
                                lastCheckAt: data?.schedule?.lastCheckAt,
                            },
                        };
                    })
                    .sort((a, b) => a.id.localeCompare(b.id));


                setLabsDocs(docs);
                setLabsSelected(new Set());
            } catch (e) {
                console.error(e);
                toast.error("Error al cargar laboratorios");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const reloadHistory = async () => {
        try {
            setHistoryLoading(true);
            const qy = query(collection(db, "reposiciones"), orderBy("createdAt", "desc"), limit(20));
            const snap = await getDocs(qy);
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setHistory(rows);
        } catch (e) {
            console.error(e);
            toast.error("No se pudo cargar el historial");
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        reloadHistory();
    }, []);

    const toggleLab = (id) =>
        setLabsSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

const toggleGroupSelected = (id) => {
  setGroupsSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
};

const clearGroupsSelection = () => setGroupsSelected(new Set());

const runGroupsManual = async ({ groupIds = [] } = {}) => {
  try {
    if (!groupIds.length) {
      toast.warn("Seleccioná al menos un grupo");
      return;
    }

    if (!selectedSucursales.size) {
      toast.warn("Seleccioná al menos una sucursal para la corrida manual");
      return;
    }

    if (!GROUPS_RUN_URL || GROUPS_RUN_URL.includes("<TU_URL>")) {
      toast.error("Falta configurar GROUPS_RUN_URL");
      return;
    }

    if (!GROUPS_RUN_TOKEN) {
      toast.error("Falta VITE_REPO_GROUPS_TOKEN en el .env del frontend");
      return;
    }

    setGroupsRunning(true);

    const sucArr = Array.from(selectedSucursales).sort();

    // Ejecuta 1 llamada por sucursal seleccionada (tu backend espera "suc" único)
    for (const suc of sucArr) {
      setGroupRunId(`suc:${suc}`);

      const { data } = await axios.post(
        GROUPS_RUN_URL,
        {
          suc,
          groupIds,
          // opcional: mailOverrideTo: ["alexk@xeroo.ai"],
          // opcional: skipHistory: true
        },
        {
          timeout: 540000,
          headers: {
            Authorization: `Bearer ${GROUPS_RUN_TOKEN}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!data?.ok) {
        console.error("[GROUPS][MANUAL] ok=false", data);
        throw new Error("La función respondió ok=false");
      }
    }

    toast.success(`Reposición de grupos enviada (${sucArr.join(", ")}) 🚀`);

    // refresca historial (asumiendo que tu backend guarda en "reposiciones")
    await reloadHistory();

    // opcional: limpiar selección
    setGroupsSelected(new Set());
  } catch (e) {
    console.error(e);
    toast.error(`Error al ejecutar grupos: ${e?.message || "unknown"}`);
  } finally {
    setGroupRunId(null);
    setGroupsRunning(false);
  }
};

// Ejecutar 1 grupo desde la fila
const runSingleGroupNow = async (g) => {
  if (!g?.id) return;
  await runGroupsManual({ groupIds: [g.id] });
};

// Ejecutar seleccionados
const runSelectedGroupsNow = async () => {
  const ids = Array.from(groupsSelected);
  await runGroupsManual({ groupIds: ids });
};



    function dedupeBySearchKey(arr) {
        const seen = new Set();
        const out = [];
        for (const l of arr) {
            if (seen.has(l.searchKey)) continue;
            seen.add(l.searchKey);
            out.push(l);
        }
        return out;
    }


    const filteredLabs = useMemo(() => {
        const q = normalize(search);
        if (!q) return dedupeBySearchKey(labsDocs);

        return dedupeBySearchKey(
            labsDocs.filter((l) => l.searchKey.includes(q))
        );
    }, [search, labsDocs]);


    const filteredLabsDueToday = useMemo(() => {
  if (!filterDueTodayOnly) return filteredLabs;
  

  return filteredLabs.filter((l) => {
    const badge = computeDueBadgeForLab(l); // 👈 tu helper
    return badge?.dueToday === true;
  });
}, [filteredLabs, filterDueTodayOnly]);


const dueTodayCount = useMemo(() => {
  // cuenta los que corresponden hoy usando la misma lógica
  return filteredLabs.filter((l) => computeDueBadgeForLab(l)?.dueToday === true).length;
}, [filteredLabs]);




const selectAllFiltered = () => {
  const next = new Set(labsSelected);
  filteredLabsDueToday.forEach((l) => next.add(l.id));
  setLabsSelected(next);
};

    const clearSelection = () => setLabsSelected(new Set());

    // ENVIO CONSOLIDADO: calcula y envia 1 correo por sucursal, 1 Excel por correo.
    useEffect(() => {
      (async () => {
        try {
          const snap = await getDoc(doc(db, "reposicion_consolidada_schedule", "principal"));
          if (snap.exists()) {
            const d = snap.data() || {};
            setConsolidadoSchedule({
              enabled: d.enabled === true,
              startISO: d.startISO || "",
              everyDays: Number(d.everyDays) || 7,
              labs: Array.isArray(d.labs) ? d.labs : [],
              sucursales: Array.isArray(d.sucursales) ? d.sucursales : ["1","2","3"],
              lastRunYMD: d.lastRunYMD || "",
              lastRunStatus: d.lastRunStatus || "",
            });
          }
        } catch(e) { console.error(e); toast.error("No se pudo cargar programación consolidada"); }
        finally { setConsolidadoScheduleLoaded(true); }
      })();
    }, []);

    const guardarProgramacionConsolidada = async () => {
      const c = consolidadoSchedule;
      if (c.enabled && (!/^\d{4}-\d{2}-\d{2}$/.test(c.startISO) ||
          !Number.isInteger(Number(c.everyDays)) || Number(c.everyDays) < 1 ||
          !c.labs.length || !c.sucursales.length)) {
        toast.warn("Para automatizar indicá fecha, frecuencia, laboratorios y sucursales");
        return;
      }
      setConsolidadoScheduleSaving(true);
      try {
        await setDoc(doc(db,"reposicion_consolidada_schedule","principal"), {
          enabled: c.enabled === true,
          startISO: c.startISO || null,
          everyDays: Number(c.everyDays) || 7,
          timezone: TZ,
          labs: c.labs,
          sucursales: c.sucursales,
          updatedAt: Date.now(),
        }, {merge:true});
        toast.success("Programación consolidada guardada");
      } catch(e) { console.error(e); toast.error("Error guardando la programación"); }
      finally { setConsolidadoScheduleSaving(false); }
    };

    const enviarRotacionConsolidada = async () => {
        if (!labsSelected.size) return toast.warn("Seleccioná al menos un laboratorio");
        if (!selectedSucursales.size) return toast.warn("Seleccioná al menos una sucursal");
        if (!window.confirm(`¿Enviar pedidos consolidados de ${labsSelected.size} laboratorios a ${selectedSucursales.size} sucursal(es)?`)) return;
        try {
            setRotacionLoading(true);
            setRotacionEnvioResult(null);
            const { data } = await axios.post(
                ROTACION_SEND_URL,
                { labs: Array.from(labsSelected), sucursales: Array.from(selectedSucursales).sort() },
                { timeout: 540000, headers: { "Content-Type": "application/json" } }
            );
            setRotacionEnvioResult(data);
            await reloadHistory();
            if (data?.ok) toast.success(`Enviados ${data.enviados} correos consolidados`);
            else toast.error(`Enviados ${data?.enviados || 0}; errores ${data?.errores || 0}. Revisá los resultados.`);
        } catch (e) {
            console.error("[ENVIO CONSOLIDADO]", e);
            const payload = e?.response?.data;
            if (payload?.resultados) setRotacionEnvioResult(payload);
            await reloadHistory();
            toast.error(payload?.error || e?.message || "Error en el envío consolidado");
        } finally {
            setRotacionLoading(false);
        }
    };

    // DESCARGA CONSOLIDADA: independiente del envío y de los schedules.
    const generarRotacionConsolidada = async () => {
        if (!labsSelected.size) return toast.warn("Seleccioná al menos un laboratorio");
        if (!selectedSucursales.size) return toast.warn("Seleccioná al menos una sucursal");
        try {
            setRotacionLoading(true);
            setRotacionFiles([]);
            const { data } = await axios.post(
                ROTACION_DOWNLOAD_URL,
                {
                    labs: Array.from(labsSelected),
                    sucursales: Array.from(selectedSucursales).sort(),
                },
                { timeout: 540000, headers: { "Content-Type": "application/json" } }
            );
            if (!data?.ok || !Array.isArray(data.files)) {
                throw new Error(data?.error || "La función no devolvió archivos");
            }
            setRotacionFiles(data.files);
            toast.success("Archivos generados. Descargalos por sucursal.");
        } catch (e) {
            console.error("[ROTACION CONSOLIDADA]", e);
            toast.error(e?.response?.data?.error || e?.message || "Error generando rotación");
        } finally {
            setRotacionLoading(false);
        }
    };

    const descargarArchivoRotacion = (f) => {
        if (!f?.base64) return toast.warn("Archivo sin datos");
        const binary = atob(f.base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = f.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
    };

    const runNow = async () => {
        try {
            if (!labsSelected.size) {
                toast.warn("Seleccioná al menos un laboratorio");
                return;
            }

            if (!selectedSucursales.size) {
                toast.warn("Seleccioná al menos una sucursal para la corrida manual");
                return;
            }

            setLoading(true);

            const nowMs = Date.now();
            const batch = writeBatch(db);

            // Dejar activos sólo los labs seleccionados (como antes)
            labsDocs.forEach(({ id }) => {
                batch.set(
                    doc(db, "laboratorios_envio", id), // 👈 id crudo
                    {
                        active: labsSelected.has(id),
                        createdAt: nowMs,
                    },
                    { merge: true }
                );
            });

            await batch.commit();

            const labsArr = Array.from(labsSelected).sort();
            const sucArr = Array.from(selectedSucursales).sort();

            // Un solo doc de reposición para toda la corrida
            const reqRef = doc(collection(db, "reposiciones"));
            await setDoc(reqRef, {
                createdAt: Date.now(),
                status: "pending",
                labs: labsArr,
                sucursales: sucArr,
                source: "frontend",
            });

            const baseUrl = "https://assistantfarmaciasgetdata-ieitjwau4a-uc.a.run.app";

  

            // Pasamos labs y sucursales como query params
            const url =
                `${baseUrl}?runId=${encodeURIComponent(reqRef.id)}` +
                `&labs=${encodeURIComponent(labsArr.join(","))}` +
                `&sucursales=${encodeURIComponent(sucArr.join(","))}`;

            const { data } = await axios.get(url, { timeout: 540000 });

            const ok = data?.ok === true;
            const patch = {
                status: ok ? "enviado" : "error",
                responseAt: Date.now(),
                rawResponseOk: !!data?.ok,
            };

            if (data && typeof data.details !== "undefined") patch.details = data.details;
            if (data && typeof data.totals !== "undefined") patch.totals = data.totals;

            await setDoc(reqRef, patch, { merge: true });

            if (ok) {
                const labelSuc =
                    sucArr.length === 3
                        ? "todas las sucursales"
                        : `sucursales ${sucArr.join(", ")}`;
                toast.success(`Reposición enviada (${labelSuc}) 🚀`);
            } else {
                toast.error("La función respondió ok=false, revisá los logs");
            }

            await reloadHistory();
            setLabsSelected(new Set());
        } catch (e) {
            console.error(e);
            toast.error("Error al enviar reposición");
        } finally {
            setLoading(false);
        }
    };




    const openDetail = async (id) => {
        try {
            setDetailOpenId(id);
            const ref = doc(db, "reposiciones", id);
            const snap = await getDoc(ref);
            if (snap.exists()) {
                setDetailDoc({ id, ...snap.data() });
            } else setDetailDoc(null);
        } catch (e) {
            console.error(e);
            setDetailDoc(null);
        }
    };
    const closeDetail = () => {
        setDetailOpenId(null);
        setDetailDoc(null);
    };

    const onChangeScheduleField = (id, field, value) => {
        setLabsDocs((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                const sch = { ...(r.schedule || {}) };
                sch[field] = value;
                if (!sch.timezone) sch.timezone = TZ;
                return { ...r, schedule: sch };
            })
        );
    };

    // NUEVO: actualizar diasVentana en el estado local
    const onChangeDiasVentana = (id, value) => {
        setLabsDocs((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                return {
                    ...r,
                    diasVentana: value === "" ? "" : Number(value),
                };
            })
        );
    };

    const onToggleSucursal = (labId, sucId, value) => {
        setLabsDocs((prev) =>
            prev.map((r) => {
                if (r.id !== labId) return r;
                return {
                    ...r,
                    sucursales: {
                        ...r.sucursales,
                        [sucId]: {
                            ...(r.sucursales?.[sucId] || {}),
                            enabled: value,
                        },
                    },
                };
            })
        );
    };

    const onChangeSucScheduleField = (labId, sucId, field, value) => {
        setLabsDocs((prev) =>
            prev.map((r) => {
                if (r.id !== labId) return r;
                const suc = r.sucursales?.[sucId] || {};
                const sched = suc.schedule || {};
                return {
                    ...r,
                    sucursales: {
                        ...r.sucursales,
                        [sucId]: {
                            ...suc,
                            schedule: {
                                ...sched,
                                [field]: value,
                            },
                        },
                    },
                };
            })
        );
    };

    const onChangeOwnerSuc = (labId, ownerSuc) => {
        setLabsDocs((prev) =>
            prev.map((r) =>
                r.id === labId ? { ...r, ownerSuc } : r
            )
        );
    };



    const saveSchedule = async (lab) => {
        setSavingLabId(lab.id);
        try {
            // validaciones por sucursal (si querés)
            Object.entries(lab.sucursales || {}).forEach(([sucId, sc]) => {
                const sched = sc.schedule || {};
                if (sc.enabled && sched.enabled) {
                    if (!sched.startISO)
                        throw new Error(
                            `Elegí fecha inicio para sucursal ${sucId} (${lab.display}).`
                        );
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(sched.startISO)))
                        throw new Error(
                            `Formato de fecha inválido para sucursal ${sucId} (${lab.display}).`
                        );
                    const n = Number(sched.everyDays || 0);
                    if (!n || n <= 0)
                        throw new Error(
                            `Frecuencia (días) debe ser > 0 para sucursal ${sucId} (${lab.display}).`
                        );
                }
            });

            const diasVentanaNum =
                lab.diasVentana === "" || lab.diasVentana === null
                    ? null
                    : Number(lab.diasVentana);

            // armar config.sucursales para guardar
            const sucursalesToSave = {};
            Object.entries(lab.sucursales || {}).forEach(([sucId, sc]) => {
                sucursalesToSave[sucId] = {
                    enabled: !!sc.enabled,
                    schedule: {
                        enabled: !!sc.schedule?.enabled,
                        startISO: sc.schedule?.startISO || null,
                        everyDays: sc.schedule?.everyDays
                            ? Number(sc.schedule.everyDays)
                            : null,
                    },
                };
            });

            await setDoc(
                doc(db, "laboratorios_envio", lab.id), // 👈 lab.id = d.id crudo
                {
                    display: lab.display || lab.id,
                    active: !!lab.active,
                    config: {
                        diasVentana: Number.isFinite(diasVentanaNum)
                            ? diasVentanaNum
                            : null,
                        ownerSuc: lab.ownerSuc || null,
                        sucursales: sucursalesToSave,
                    },
                },
                { merge: true }
            );


            toast.success(`Guardado ${lab.display || lab.id}`);

            setLabsDocs((prev) =>
                prev.map((r) => {
                    if (r.id !== lab.id) return r;

                    const diasDef = Number.isFinite(Number(lab.diasVentana))
                        ? Number(lab.diasVentana)
                        : 30;

                    return {
                        ...r,
                        diasVentana: diasDef,
                        diasVentanaOriginal: diasDef,

                        ownerSuc: lab.ownerSuc || "",
                        ownerSucOriginal: lab.ownerSuc || "",        // 👈 NUEVO

                        sucursales: lab.sucursales || {},
                        sucursalesOriginal: lab.sucursales || {},    // 👈 NUEVO
                    };
                })
            );

        } catch (e) {
            toast.error(`Error guardando ${lab.display || lab.id}: ${e.message}`);
        } finally {
            setSavingLabId(null);
        }
    };

    const extractEmails = (doc) => {
        const byDetails = safeArray(doc?.details?.emails);
        if (byDetails.length) return byDetails;
        return safeArray(doc?.emails);
    };

    const computeLabsCountFallback = (doc) => {
        const labsCountFromTotals = safeNum(doc?.totals?.labsCount);
        if (labsCountFromTotals > 0) return labsCountFromTotals;
        const emails = extractEmails(doc);
        const setLabs = new Set(
            emails
                .map((e) => normalize(e?.labNorm || e?.labDisplay || ""))
                .filter(Boolean)
        );
        return setLabs.size || 0;
    };





    const renderedList = useMemo(
        () =>
  filteredLabsDueToday.map((l) => {
const sch = l.schedule || {};            // ✅ para los inputs (no lo saques)
const badge = computeDueBadgeForLab(l);  // ✅ para badge
const dueToday = badge.dueToday;
const nextISO = badge.nextISO;

const modeLabel =
  badge.mode === "individual" ? "por sucursal" :
  badge.mode === "general" ? "general" :
  "";

                const diasVentanaCurrent = l.diasVentana === "" ? "" : Number(l.diasVentana);
                const diasVentanaOriginal = l.diasVentanaOriginal === "" ? "" : Number(l.diasVentanaOriginal);

                const everyDaysCurrent = sch.everyDays ?? "";
                const everyDaysOriginal = sch.everyDaysOriginal ?? "";

                // 👇 NUEVO: comparar dueño unificado y sucursales
                const ownerChanged = (l.ownerSuc || "") !== (l.ownerSucOriginal || "");

                const sucursalesChanged =
                    JSON.stringify(l.sucursales || {}) !==
                    JSON.stringify(l.sucursalesOriginal || {});

                const dirty =
                    (!!sch.enabled !== !!sch.enabledOriginal) ||
                    (sch.startISO || "") !== (sch.startISOOriginal || "") ||
                    everyDaysCurrent !== everyDaysOriginal ||
                    diasVentanaCurrent !== diasVentanaOriginal ||
                    ownerChanged ||                              // 👈 NUEVO
                    sucursalesChanged;                          // 👈 NUEVO

                return (
                    <div key={l.id} className="flex flex-col gap-2 py-2 border-b last:border-b-0">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-success"
                                    checked={labsSelected.has(l.id)}
                                    onChange={() => toggleLab(l.id)}
                                />
                                <span className="font-medium">{l.display || l.id}</span>
                            </label>

                     <div className="flex items-center gap-2">
  {dueToday ? (
    <span className="badge badge-warning">
      📅 Corresponde hoy
      {modeLabel ? <span className="ml-2 opacity-70 text-[11px]">({modeLabel})</span> : null}
    </span>
  ) : nextISO ? (
    <span className="badge">
      Próxima: {nextISO.split("-").reverse().join("/")}
      {modeLabel ? <span className="ml-2 opacity-70 text-[11px]">({modeLabel})</span> : null}
    </span>
  ) : (
    <span className="badge badge-ghost">Sin programación</span>
  )}
</div>

                        </div>

                        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center pl-6">
                            {/* Automatizar / inicio / cada X días */}
                            <div className="flex flex-wrap items-center gap-3">
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={!!sch.enabled}
                                        onChange={(e) =>
                                            onChangeScheduleField(
                                                l.id,
                                                "enabled",
                                                e.target.checked
                                            )
                                        }
                                    />
                                    <span>Automatizar</span>
                                </label>

                                <label className="text-sm">
                                    Inicio
                                    <input
                                        type="date"
                                        className="ml-2 border rounded px-2 py-1"
                                        value={String(sch.startISO || "")}
                                        onChange={(e) =>
                                            onChangeScheduleField(
                                                l.id,
                                                "startISO",
                                                e.target.value
                                            )
                                        }
                                    />
                                </label>

                                <label className="text-sm">
                                    Cada (días)
                                    <input
                                        type="number"
                                        min={1}
                                        className="ml-2 w-24 border rounded px-2 py-1"
                                        value={sch.everyDays ?? ""}
                                        onChange={(e) =>
                                            onChangeScheduleField(
                                                l.id,
                                                "everyDays",
                                                e.target.value
                                                    ? Number(e.target.value)
                                                    : ""
                                            )
                                        }
                                    />
                                </label>
                            </div>

                            {/* NUEVO: Días de ventana */}
                            <label className="text-sm flex items-center">
                                Días de venta a considerar
                                <input
                                    type="number"
                                    min={1}
                                    className="ml-2 w-24 border rounded px-2 py-1"
                                    value={l.diasVentana ?? ""}
                                    onChange={(e) =>
                                        onChangeDiasVentana(
                                            l.id,
                                            e.target.value ? Number(e.target.value) : ""
                                        )
                                    }
                                />
                            </label>

                            <button
                                disabled={savingLabId === l.id || !dirty}
                                onClick={() => saveSchedule(l)}
                                className={
                                    "btn btn-sm border-none text-white " +
                                    (savingLabId === l.id || !dirty
                                        ? "bg-emerald-900 opacity-40 cursor-not-allowed"
                                        : "bg-emerald-600 hover:bg-emerald-500")
                                }
                            >
                                {savingLabId === l.id ? "Guardando…" : "Guardar"}
                            </button>

                            {/* Dueña del lab (unificado) */}
                            <div className="flex items-center gap-2 text-sm">
                                <span>Unificar pedido en sucursal: </span>
                                <select
                                    className="select select-sm select-bordered w-32"
                                    value={l.ownerSuc || ""}
                                    onChange={(e) => onChangeOwnerSuc(l.id, e.target.value || "")}
                                >
                                    <option value="">Sin unificar</option>
                                    <option value="1">Moscovich</option>
                                    <option value="2">Guberman</option>
                                    <option value="3">Sunchales</option>
                                </select>
                            </div>

                            {/* Por sucursal */}
                            <div className="flex flex-col gap-2 mt-2">
                                {["1", "2", "3"].map((sucId) => {
                                    const sucMeta = {
                                        "1": "Moscovich",
                                        "2": "Guberman",
                                        "3": "Sunchales",
                                    }[sucId];

                                    const sucCfg = l.sucursales?.[sucId] || {
                                        enabled: true,
                                        schedule: { enabled: false, startISO: "", everyDays: "" },
                                    };
                                    const schSuc = sucCfg.schedule || {};

                                    return (
                                        <div
                                            key={sucId}
                                            className="flex flex-wrap items-center gap-3 text-sm pl-2 border-l border-base-300"
                                        >
                                            <label className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-xs checkbox-success"
                                                    checked={!!sucCfg.enabled}
                                                    onChange={(e) =>
                                                        onToggleSucursal(l.id, sucId, e.target.checked)
                                                    }
                                                />
                                                <span className="font-medium">
                                                    Sucursal {sucId} – {sucMeta}
                                                </span>
                                            </label>

                                            <label className="flex items-center gap-1">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-xs"
                                                    checked={!!schSuc.enabled}
                                                    onChange={(e) =>
                                                        onChangeSucScheduleField(
                                                            l.id,
                                                            sucId,
                                                            "enabled",
                                                            e.target.checked
                                                        )
                                                    }
                                                    disabled={!sucCfg.enabled}
                                                />
                                                <span>Programar</span>
                                            </label>

                                            <label>
                                                Inicio
                                                <input
                                                    type="date"
                                                    className="ml-2 border rounded px-2 py-1"
                                                    value={schSuc.startISO || ""}
                                                    onChange={(e) =>
                                                        onChangeSucScheduleField(
                                                            l.id,
                                                            sucId,
                                                            "startISO",
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={!sucCfg.enabled || !schSuc.enabled}
                                                />
                                            </label>

                                            <label>
                                                Cada (días)
                                                <input
                                                    type="number"
                                                    min={1}
                                                    className="ml-2 w-20 border rounded px-2 py-1"
                                                    value={schSuc.everyDays ?? ""}
                                                    onChange={(e) =>
                                                        onChangeSucScheduleField(
                                                            l.id,
                                                            sucId,
                                                            "everyDays",
                                                            e.target.value
                                                                ? Number(e.target.value)
                                                                : ""
                                                        )
                                                    }
                                                    disabled={!sucCfg.enabled || !schSuc.enabled}
                                                />
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>




                        </div>
                    </div>
                );
            }),
        [filteredLabsDueToday, labsSelected, savingLabId]
    );

    const markAsDelivered = async (id, opts = {}) => {
        try {
            // opcionalmente podemos tomar alsoUpdateDetail para actualizar el popup
            const { alsoUpdateDetail = false } = opts;

            await setDoc(
                doc(db, "reposiciones", id),
                {
                    status: "entregado",
                    deliveredAt: Date.now(),
                },
                { merge: true }
            );

            toast.success("Marcado como entregado ✅");

            // refresco historial
            await reloadHistory();

            // si estoy viendo el popup de este mismo pedido, actualizo ahí también
            if (alsoUpdateDetail && detailDoc && detailDoc.id === id) {
                setDetailDoc((prev) => prev ? { ...prev, status: "entregado", deliveredAt: Date.now() } : prev);
            }
        } catch (e) {
            console.error(e);
            toast.error("No se pudo marcar como entregado");
        }
    };


    /* UI ---------------- */
    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold mb-4">Reposición de Stock</h1>

            <div className="bg-base-100 p-4 rounded shadow mb-6">
                <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar laboratorio…"
                        className="input input-bordered w-full md:w-80"
                    />
                    <div className="flex gap-2">
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={selectAllFiltered}
                            disabled={loading || !filteredLabs.length}
                        >
                            Seleccionar filtrados
                        </button>
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={clearSelection}
                            disabled={loading || labsSelected.size === 0}
                        >
                            Limpiar selección
                        </button>
                      <button
  className={"btn btn-outline btn-sm " + (filterDueTodayOnly ? "btn-active" : "")}
  onClick={() => setFilterDueTodayOnly((p) => !p)}
  disabled={loading || !labsDocs.length}
  title="Muestra sólo laboratorios que corresponden hoy (por sucursal si está bien configurado; si no, general)"
>
  📅 Sólo hoy ({dueTodayCount})
</button>
                    </div>
                </div>

                {loading && <p>Cargando…</p>}
                {!loading && !labsDocs.length && <p>No hay datos.</p>}

                <div className="max-h-[50vh] overflow-y-auto border rounded p-3">
               {filterDueTodayOnly && filteredLabsDueToday.length === 0 ? (
  <div className="text-sm opacity-70 py-6 text-center">
    Para hoy no corresponde ninguno.
  </div>
) : (
  <div className="flex flex-col">{renderedList}</div>
)}
                </div>

                <div className="mt-4 flex justify-between items-center text-sm opacity-70">
                    <span>Total labs: {labsDocs.length}</span>
                    <span>Seleccionados: {labsSelected.size}</span>
                </div>
            </div>

            {/* selección global de sucursales para la corrida manual */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">Sucursales a incluir en la corrida manual:</span>

                    {["1", "2", "3"].map((sid) => {
                        const label =
                            sid === "1" ? "Moscovich" :
                                sid === "2" ? "Guberman" :
                                    sid === "3" ? "Sunchales" :
                                        `Suc ${sid}`;
                        return (
                            <label key={sid} className="flex items-center gap-1">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-xs checkbox-success"
                                    checked={selectedSucursales.has(sid)}
                                    onChange={() => toggleSucursalGlobal(sid)}
                                />
                                <span>{label}</span>
                            </label>
                        );
                    })}
                </div>

                <div className="text-xs opacity-70">
                    Si desmarcás alguna sucursal, la corrida manual sólo considerará las sucursales seleccionadas.
                </div>
            </div>

            <div className="flex gap-3 mb-8">
                <button
                    className="btn bg-[#2E8F42] border-none text-white"
                    onClick={runNow}
                    disabled={loading}
                >
                    Pedir reposición ahora
                </button>
            </div>



            {/* ENVIO MANUAL CONSOLIDADO, con descarga opcional */}
            <div className="bg-base-100 p-4 rounded shadow mb-6 border border-success/30">
                <h2 className="text-xl font-semibold mb-2">Pedido de reposición consolidado</h2>
                <p className="text-sm opacity-70 mb-3">
                    Usá los laboratorios y sucursales seleccionados arriba. Envía un solo correo
                    por sucursal con un único Excel que incluye todos los laboratorios seleccionados.
                    Mantiene mínimos, exclusiones, unificación y cálculo de depósito. No envía correos separados por laboratorio.
                </p>
                <button
                    className="btn btn-success mr-2"
                    onClick={enviarRotacionConsolidada}
                    disabled={rotacionLoading || loading || !labsSelected.size || !selectedSucursales.size}
                >
                    {rotacionLoading ? "Enviando…" : `Enviar correo consolidado (${labsSelected.size} labs)`}
                </button>
                <button
                    className="btn btn-outline"
                    onClick={generarRotacionConsolidada}
                    disabled={rotacionLoading || loading || !labsSelected.size || !selectedSucursales.size}
                >
                    {rotacionLoading ? "Procesando…" : "Descargar Excel (opcional)"}
                </button>
                {rotacionEnvioResult && (
                    <div className="mt-3 text-sm">
                        <strong>Resultado:</strong> {rotacionEnvioResult.enviados || 0} correos enviados · {rotacionEnvioResult.errores || 0} errores
                        {(rotacionEnvioResult.resultados || []).map(r => (
                            <div key={r.sucursal}>{r.nombre}: {r.status} ({r.items || 0} productos)</div>
                        ))}
                    </div>
                )}
                {!!rotacionFiles.length && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {rotacionFiles.map(f => (
                            <button key={f.sucursal} className="btn btn-outline btn-sm"
                                onClick={() => descargarArchivoRotacion(f)}>
                                Descargar {f.sucursalNombre} ({f.items} ítems, {f.unidades} unidades)
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Programación INDEPENDIENTE del pedido consolidado manual */}
            <div className="bg-base-100 p-4 rounded shadow mb-6 border border-info/30">
              <h2 className="text-xl font-semibold mb-2">Programar pedido de reposición consolidado</h2>
              <p className="text-sm opacity-70 mb-3">
                Guardá una lista fija de laboratorios y sucursales, y programá el envío automático.
                Se ejecuta a las 08:10 (Argentina) los días correspondientes. Un correo con Excel por sucursal.
                Los laboratorios incluidos en esta programación dejan de enviarse individualmente por el scheduler anterior mientras esté activa.
              </p>
              <div className="flex flex-wrap gap-4 items-end">
                <label className="flex gap-2 items-center text-sm">
                  <input type="checkbox" checked={!!consolidadoSchedule.enabled}
                    onChange={e => setConsolidadoSchedule(p => ({...p,enabled:e.target.checked}))}/>
                  Automatizar
                </label>
                <label className="text-sm">Fecha de inicio
                  <input className="input input-bordered input-sm ml-2" type="date"
                    value={consolidadoSchedule.startISO || ""}
                    onChange={e => setConsolidadoSchedule(p => ({...p,startISO:e.target.value}))}/>
                </label>
                <label className="text-sm">Cada (días)
                  <input className="input input-bordered input-sm ml-2 w-20" type="number" min="1"
                    value={consolidadoSchedule.everyDays ?? 7}
                    onChange={e => setConsolidadoSchedule(p => ({...p,everyDays:e.target.value}))}/>
                </label>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <span className="text-sm">Laboratorios guardados: <b>{consolidadoSchedule.labs.length}</b></span>
                <button className="btn btn-outline btn-sm" onClick={() => {
                  if (!labsSelected.size) return toast.warn("Seleccioná laboratorios arriba");
                  setConsolidadoSchedule(p => ({...p,labs:Array.from(labsSelected).sort()}));
                }}>Usar laboratorios seleccionados arriba</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setConsolidadoSchedule(p => ({...p,labs:[]}))}>Limpiar laboratorios programados</button>
              </div>
              {consolidadoSchedule.labs.length > 0 && (
                <div className="text-xs opacity-70 mt-1 max-h-20 overflow-y-auto">
                  {consolidadoSchedule.labs.map(id => labsDocs.find(l => l.id === id)?.display || id).join(" · ")}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-3 items-center text-sm">
                <b>Sucursales del envío programado:</b>
                {["1","2","3"].map(sid => (
                  <label key={sid} className="flex items-center gap-1">
                    <input type="checkbox" checked={consolidadoSchedule.sucursales.includes(sid)}
                      onChange={e => setConsolidadoSchedule(p => ({...p,sucursales:e.target.checked
                        ? [...new Set([...p.sucursales,sid])].sort()
                        : p.sucursales.filter(s => s !== sid)}))}/>
                    {{"1":"Moscovich","2":"Guberman","3":"Sunchales"}[sid]}
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <button className="btn btn-primary" onClick={guardarProgramacionConsolidada}
                  disabled={!consolidadoScheduleLoaded || consolidadoScheduleSaving}>
                  {consolidadoScheduleSaving ? "Guardando…" : "Guardar programación consolidada"}
                </button>
                {consolidadoSchedule.enabled && consolidadoSchedule.startISO && Number(consolidadoSchedule.everyDays) > 0 && (
                  <span className="text-sm opacity-75">Próximo envío: {nextRunISO(consolidadoSchedule.startISO,Number(consolidadoSchedule.everyDays)) || "-"} · 08:10</span>
                )}
                {consolidadoSchedule.lastRunYMD && (
                  <span className="text-xs opacity-70">Último: {consolidadoSchedule.lastRunYMD} ({consolidadoSchedule.lastRunStatus || "-"})</span>
                )}
              </div>
              <p className="text-xs opacity-60 mt-3">Los cambios no quedan activos hasta tocar Guardar. Esta opción no ejecuta un pedido al guardarla.</p>
            </div>

            <div className="bg-base-100 p-4 rounded shadow mb-6">
  <div className="flex items-center justify-between mb-3">
    <h2 className="text-xl font-semibold">Productos excluidos de reposición</h2>
    <button className="btn btn-outline btn-sm" onClick={loadExcludedSkus} disabled={excludedLoading}>
      Actualizar
    </button>
  </div>

  <div className="flex gap-2 mb-3">
    <input
      className="input input-bordered w-full md:w-80"
      placeholder="Código troquel a excluir"
      value={excludedSkuInput}
      onChange={(e) => setExcludedSkuInput(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") addExcludedSku();
      }}
    />
    <button className="btn btn-primary" onClick={addExcludedSku}>
      Agregar
    </button>
  </div>

  <div className="text-xs opacity-70 mb-3">
    Estos códidos de troquel no se incluirán en pedidos manuales ni automáticos, tanto por laboratorio como por grupos.
  </div>

  {excludedLoading && <p>Cargando…</p>}

  {!excludedLoading && !excludedSkus.length && (
    <p className="text-sm opacity-70">No hay códidos de troquel excluidos.</p>
  )}

  {!!excludedSkus.length && (
    <div className="flex flex-wrap gap-2">
      {excludedSkus.map((x) => (
        <span key={x.id} className="badge badge-outline gap-2">
          {x.sku || x.id}
          <button
            className="btn btn-xs btn-ghost"
            onClick={() => removeExcludedSku(x.sku || x.id)}
          >
            ✕
          </button>
        </span>
      ))}
    </div>
  )}
</div>




            {/* ================== GRUPOS DE PRODUCTOS ================== */}
            <div className="bg-base-100 p-4 rounded shadow mb-6">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xl font-semibold">Grupos de productos</h2>
                   <div className="flex gap-2 flex-wrap">
  <button className="btn btn-outline btn-sm" onClick={loadGroups} disabled={groupsLoading || groupsRunning}>
    Actualizar
  </button>

  <button
    className="btn btn-outline btn-sm"
    onClick={runSelectedGroupsNow}
    disabled={groupsRunning || groupsSelected.size === 0}
    title="Ejecuta reposición de los grupos seleccionados"
  >
    {groupsRunning ? "Ejecutando…" : `Ejecutar seleccionados (${groupsSelected.size})`}
  </button>

  <button
    className="btn btn-outline btn-sm"
    onClick={clearGroupsSelection}
    disabled={groupsRunning || groupsSelected.size === 0}
  >
    Limpiar selección
  </button>

  <button className="btn btn-sm btn-primary" onClick={openCreateGroup} disabled={groupsRunning}>
    Crear grupo de productos
  </button>
</div>

                </div>

                {groupsLoading && <p>Cargando grupos…</p>}
                {!groupsLoading && !groups.length && <p>No hay grupos todavía.</p>}

                {!!groups.length && (
                    <div className="overflow-x-auto">
                        <table className="table table-zebra w-full">
                          <thead>
  <tr>
    <th className="w-10"></th>
    <th>Nombre</th>
    <th>Activo</th>
    <th>Reglas</th>
<th>Programación</th>
<th>Ventana de Ventas</th>
<th className="text-right">Acciones</th>
  </tr>
</thead>

                            <tbody>
                                {groups.map((g) => {
                                    const kwCount = Array.isArray(g.keywords) ? g.keywords.length : 0;
                                    const skuCount = Array.isArray(g.skus) ? g.skus.length : 0;
                                    const gen = g.schedule || {};
                                    const genLabel =
                                        gen?.enabled && gen?.startISO && gen?.everyDays
                                            ? `General: desde ${String(gen.startISO).split("-").reverse().join("/")} · cada ${gen.everyDays}d`
                                            : "General: sin programación";

                                    return (
                                       <tr key={g.id}>
  <td>
    <input
      type="checkbox"
      className="checkbox checkbox-success checkbox-sm"
      checked={groupsSelected.has(g.id)}
      onChange={() => toggleGroupSelected(g.id)}
      disabled={groupsRunning}
      title="Seleccionar grupo"
    />
  </td>

  <td className="font-medium">{g.display || g.id}</td>

  <td>
    <span className={"badge " + (g.active === false ? "badge-ghost" : "badge-success")}>
      {g.active === false ? "No" : "Sí"}
    </span>
  </td>

  <td className="text-sm opacity-80">
    {kwCount} keywords · {skuCount} SKUs
  </td>

  <td className="text-sm opacity-80">{genLabel}</td>

  <td className="text-sm opacity-80">
  {g.windowDays ? `${g.windowDays} días` : "-"}
</td>

  <td className="text-right">
    <div className="flex justify-end gap-2 flex-wrap">
      <button
        className="btn btn-sm btn-outline"
        onClick={() => runSingleGroupNow(g)}
        disabled={groupsRunning}
        title="Ejecutar reposición de este grupo ahora"
      >
        {groupsRunning ? "Ejecutando…" : "Ejecutar ahora"}
      </button>

      <button className="btn btn-sm" onClick={() => openEditGroup(g)} disabled={groupsRunning}>
        Editar
      </button>

      <button
        className="btn btn-sm btn-error"
        disabled={groupDeletingId === g.id || groupsRunning}
        onClick={() => deleteGroup(g)}
        title="Eliminar grupo"
      >
        {groupDeletingId === g.id ? "Eliminando…" : "Eliminar"}
      </button>
    </div>
  </td>
</tr>

                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ================ MODAL CREAR/EDITAR GRUPO ================ */}
            {groupModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-base-100 rounded-lg p-0 max-w-3xl w-[96%] max-h-[90vh] flex flex-col relative">
                        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-base-100 z-10 rounded-t-lg">
                            <h3 className="text-lg font-semibold">
                                {groupForm.id ? "Editar grupo" : "Crear grupo"}
                            </h3>
                            <button className="btn btn-sm btn-ghost" onClick={closeGroupModal}>
                                Cerrar
                            </button>
                        </div>

                        <div className="px-5 py-4 overflow-y-auto flex flex-col gap-4">
                            <label className="form-control">
                                <div className="label">
                                    <span className="label-text font-medium">Nombre del grupo</span>
                                </div>
                                <input
                                    className="input input-bordered"
                                    value={groupForm.display}
                                    onChange={(e) => setGroupForm((p) => ({ ...p, display: e.target.value }))}
                                    placeholder="Ej: Dermocosmética, Antigripales, etc."
                                />
                            </label>

                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-success"
                                    checked={groupForm.active === true}
                                    onChange={(e) => setGroupForm((p) => ({ ...p, active: e.target.checked }))}
                                />
                                <span>Activo</span>
                            </label>

                            {/* Keywords */}
                            <div className="border rounded p-3">
                                <div className="font-medium mb-2">Palabras clave (keywords)</div>
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {(groupForm.keywords || []).map((kw) => (
                                        <span key={kw} className="badge badge-outline gap-2">
                                            {kw}
                                            <button className="btn btn-xs btn-ghost" onClick={() => removeKeyword(kw)}>
                                                ✕
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                <div className="flex gap-2">
                                    <input
                                        className="input input-bordered w-full"
                                        value={groupForm.keywordInput}
                                        placeholder="Escribí una keyword y tocá Agregar"
                                        onChange={(e) => setGroupForm((p) => ({ ...p, keywordInput: e.target.value }))}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                addKeywordFromInput();
                                            }
                                        }}
                                    />
                                    <button className="btn btn-outline" onClick={addKeywordFromInput}>
                                        Agregar
                                    </button>
                                </div>

                                <div className="text-xs opacity-70 mt-2">
                                    Tip: cargá palabras que aparezcan en la descripción del producto (ej: “dermaglos”, “fps”, “ibuprofeno”).
                                </div>
                            </div>

                            {/* SKUs */}
                            <div className="border rounded p-3">
                                <div className="font-medium mb-2">Códigos Troquel específicos</div>

                                <div className="flex flex-wrap gap-2 mb-2">
                                    {(groupForm.skus || []).map((sku) => (
                                        <span key={sku} className="badge badge-outline gap-2">
                                            {sku}
                                            <button className="btn btn-xs btn-ghost" onClick={() => removeSku(sku)}>
                                                ✕
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                <div className="flex gap-2">
                                    <input
                                        className="input input-bordered w-full"
                                        value={groupForm.skuInput}
                                        placeholder="Ej: 3833311, 3833313"
                                        onChange={(e) => setGroupForm((p) => ({ ...p, skuInput: e.target.value }))}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                addSkuFromInput();
                                            }
                                        }}
                                    />
                                    <button className="btn btn-outline" onClick={addSkuFromInput}>
                                        Agregar
                                    </button>
                                </div>

                                <div className="text-xs opacity-70 mt-2">
                                    Podés pegar varios códigos de troquel separados por coma, punto y coma o salto de línea.
                                    El match por Código de troquel es exacto y se combina con keywords como “keyword O SKU”.
                                </div>
                            </div>

                            {/* Schedule general */}
                            <div className="border rounded p-3">
                                <div className="font-medium mb-2">Programación general (fallback)</div>

                 


                                <label className="flex items-center gap-2 text-sm mb-2">
                                    <input
                                        type="checkbox"
                                        checked={!!groupForm.schedule?.enabled}
                                        onChange={(e) =>
                                            setGroupForm((p) => ({
                                                ...p,
                                                schedule: { ...(p.schedule || {}), enabled: e.target.checked },
                                            }))
                                        }
                                    />
                                    <span>Automatizar</span>
                                </label>

                                <div className="flex flex-wrap items-center gap-3">
                                    <label className="text-sm">
                                        Inicio
                                        <input
                                            type="date"
                                            className="ml-2 border rounded px-2 py-1"
                                            value={groupForm.schedule?.startISO || ""}
                                            onChange={(e) =>
                                                setGroupForm((p) => ({
                                                    ...p,
                                                    schedule: { ...(p.schedule || {}), startISO: e.target.value },
                                                }))
                                            }
                                            disabled={!groupForm.schedule?.enabled}
                                        />
                                    </label>

                                    <label className="text-sm">
                                        Cada (días)
                                        <input
                                            type="number"
                                            min={1}
                                            className="ml-2 w-24 border rounded px-2 py-1"
                                            value={groupForm.schedule?.everyDays ?? ""}
                                            onChange={(e) =>
                                                setGroupForm((p) => ({
                                                    ...p,
                                                    schedule: {
                                                        ...(p.schedule || {}),
                                                        everyDays: e.target.value ? Number(e.target.value) : "",
                                                    },
                                                }))
                                            }
                                            disabled={!groupForm.schedule?.enabled}
                                        />
                                    </label>
                                </div>


<div className="mt-3">
  <label className="text-sm">
    Ventana de días de venta
    <input
      type="number"
      min={1}
      className="ml-2 w-24 border rounded px-2 py-1"
      value={groupForm.windowDays ?? ""}
      onChange={(e) =>
        setGroupForm((p) => ({
          ...p,
          windowDays: e.target.value ? Number(e.target.value) : "",
        }))
      }
    />
  </label>

  <div className="text-xs opacity-70 mt-1">
    Si no se define por sucursal, se usa esta ventana general.
  </div>
</div>



                            </div>

                            {/* Por sucursal */}
                            <div className="border rounded p-3">
                                <div className="font-medium mb-2">Configuración por sucursal</div>

                                {/* owner opcional */}
                                <div className="flex items-center gap-2 text-sm mb-3">
                                    <span>Unificar pedido en sucursal:</span>
                                    <select
                                        className="select select-sm select-bordered w-44"
                                        value={groupForm.config?.ownerSuc || ""}
                                        onChange={(e) =>
                                            setGroupForm((p) => ({
                                                ...p,
                                                config: { ...(p.config || {}), ownerSuc: e.target.value || "" },
                                            }))
                                        }
                                    >
                                        <option value="">Sin unificar</option>
                                        <option value="1">Moscovich</option>
                                        <option value="2">Guberman</option>
                                        <option value="3">Sunchales</option>
                                    </select>
                                </div>

                                {["1", "2", "3"].map((sid) => {
                                    const label = sid === "1" ? "Moscovich" : sid === "2" ? "Guberman" : "Sunchales";
                                    const sc = groupForm.config?.sucursales?.[sid] || {};
                                    const sch = sc.schedule || {};

                                    return (
                                        <div key={sid} className="flex flex-wrap items-center gap-3 text-sm pl-2 border-l border-base-300 mb-2">
                                            <label className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-xs checkbox-success"
                                                    checked={sc.enabled !== false}
                                                    onChange={(e) =>
                                                        setGroupForm((p) => ({
                                                            ...p,
                                                            config: {
                                                                ...(p.config || {}),
                                                                sucursales: {
                                                                    ...(p.config?.sucursales || {}),
                                                                    [sid]: {
                                                                        ...(p.config?.sucursales?.[sid] || {}),
                                                                        enabled: e.target.checked,
                                                                        schedule: { ...(p.config?.sucursales?.[sid]?.schedule || {}) },
                                                                    },
                                                                },
                                                            },
                                                        }))
                                                    }
                                                />
                                                <span className="font-medium">
                                                    Sucursal {sid} – {label}
                                                </span>
                                            </label>

                                            <label className="flex items-center gap-1">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-xs"
                                                    checked={!!sch.enabled}
                                                    onChange={(e) =>
                                                        setGroupForm((p) => ({
                                                            ...p,
                                                            config: {
                                                                ...(p.config || {}),
                                                                sucursales: {
                                                                    ...(p.config?.sucursales || {}),
                                                                    [sid]: {
                                                                        ...(p.config?.sucursales?.[sid] || {}),
                                                                        schedule: {
                                                                            ...(p.config?.sucursales?.[sid]?.schedule || {}),
                                                                            enabled: e.target.checked,
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        }))
                                                    }
                                                    disabled={sc.enabled === false}
                                                />
                                                <span>Programar</span>
                                            </label>

                                            <label>
                                                Inicio
                                                <input
                                                    type="date"
                                                    className="ml-2 border rounded px-2 py-1"
                                                    value={sch.startISO || ""}
                                                    onChange={(e) =>
                                                        setGroupForm((p) => ({
                                                            ...p,
                                                            config: {
                                                                ...(p.config || {}),
                                                                sucursales: {
                                                                    ...(p.config?.sucursales || {}),
                                                                    [sid]: {
                                                                        ...(p.config?.sucursales?.[sid] || {}),
                                                                        schedule: {
                                                                            ...(p.config?.sucursales?.[sid]?.schedule || {}),
                                                                            startISO: e.target.value,
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        }))
                                                    }
                                                    disabled={sc.enabled === false || !sch.enabled}
                                                />
                                            </label>

                                            <label>
                                                Cada (días)
                                                <input
                                                    type="number"
                                                    min={1}
                                                    className="ml-2 w-20 border rounded px-2 py-1"
                                                    value={sch.everyDays ?? ""}
                                                    onChange={(e) =>
                                                        setGroupForm((p) => ({
                                                            ...p,
                                                            config: {
                                                                ...(p.config || {}),
                                                                sucursales: {
                                                                    ...(p.config?.sucursales || {}),
                                                                    [sid]: {
                                                                        ...(p.config?.sucursales?.[sid] || {}),
                                                                        schedule: {
                                                                            ...(p.config?.sucursales?.[sid]?.schedule || {}),
                                                                            everyDays: e.target.value ? Number(e.target.value) : "",
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        }))
                                                    }
                                                    disabled={sc.enabled === false || !sch.enabled}
                                                />
                                            </label>

<label>
  Ventana días
  <input
    type="number"
    min={1}
    className="ml-2 w-20 border rounded px-2 py-1"
    value={sc.windowDays ?? ""}
    onChange={(e) =>
      setGroupForm((p) => ({
        ...p,
        config: {
          ...(p.config || {}),
          sucursales: {
            ...(p.config?.sucursales || {}),
            [sid]: {
              ...(p.config?.sucursales?.[sid] || {}),
              windowDays: e.target.value ? Number(e.target.value) : "",
            },
          },
        },
      }))
    }
  />
</label>



                                        </div>
                                    );
                                })}

                                <div className="text-xs opacity-70 mt-2">
                                    Si una sucursal está habilitada pero no tiene programación propia, luego usaremos como fallback la programación general del grupo.
                                </div>
                            </div>
                        </div>

                        <div className="px-5 py-3 border-t flex justify-end gap-2">
                            <button className="btn btn-ghost" onClick={closeGroupModal}>
                                Cancelar
                            </button>
                            <button className="btn btn-primary" onClick={saveGroup} disabled={groupSaving}>
                                {groupSaving ? "Guardando…" : "Guardar"}
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {/* historial */}
            <div className="bg-base-100 p-4 rounded shadow">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xl font-semibold">Historial de pedidos</h2>
                    <div className="flex gap-2">
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={reloadHistory}
                            disabled={historyLoading}
                        >
                            Actualizar
                        </button>
                    </div>
                </div>

                {historyLoading && <p>Cargando historial…</p>}
                {!historyLoading && !history.length && <p>No hay pedidos todavía.</p>}

                {!!history.length && (
                    <div className="overflow-x-auto">
                        <table className="table table-zebra w-full">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Estado</th>
                                    <th>Período</th>
                                    <th>Resumen</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((h) => {
                                    const emailsCount = safeNum(h?.totals?.emailsCount);
                                    const productosCount = safeNum(h?.totals?.productosCount);
                                    const unidadesTotal = safeNum(h?.totals?.unidadesTotal);
                                    const labsCount =
                                        h?.totals?.labsCount > 0
                                            ? h.totals.labsCount
                                            : computeLabsCountFallback(h);

                                            const outTotals = h?.totals?.outcomes || {};
const sentN = safeNum(outTotals?.sent);
const skipN = safeNum(outTotals?.skipped);
const errN  = safeNum(outTotals?.error);

const outcomeLabel =
  (sentN || skipN || errN)
    ? `Enviados: ${sentN} · Sin productos: ${skipN} · Error: ${errN}`
    : null;


                                    return (
                                        <tr key={h.id}>
                                            <td>
                                                <div className="flex flex-col">
                                                    <div className="flex items-center flex-wrap">
                                                        <span>{fmtDateTime(h.createdAt)}</span>
                                                        {ageBadge(h)}
                                                    </div>
                                                    {h.finishedAt ? (
                                                        <span className="text-xs opacity-70">
                                                            Fin: {fmtDateTime(h.finishedAt)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </td>
                                         <td>
  <div className="flex flex-col gap-1">
    <span className={"badge " + statusBadgeClass(h.status)}>
      {statusToEs(h.status)}
    </span>

    {isGroupsRun(h) && (
      <span className="badge badge-secondary badge-sm">
        GRUPOS
      </span>
    )}
  </div>
</td>

                                            <td className="text-sm">
                                                {fmtYMD(h?.period?.start)} →{" "}
                                                {fmtYMD(h?.period?.end)}
                                            </td>
                             <td className="text-sm">
  {[
    `Emails: ${emailsCount || 0}`,
    `Labs: ${labsCount || 0}`,
  ].join(" · ")}

  {h?.totals?.outcomes && (
    <div className="text-xs opacity-70 mt-1">
      Enviados: {safeNum(h.totals.outcomes.sent)} ·
      Sin productos: {safeNum(h.totals.outcomes.skipped)} ·
      Error: {safeNum(h.totals.outcomes.error)}
    </div>
  )}

  {isGroupsRun(h) && (
    <div className="text-xs opacity-60 mt-1">
      Tipo: Reposición por grupos
    </div>
  )}
</td>


                                            <td className="text-right">
                                                <div className="flex flex-col items-end gap-2">
                                                    <button
                                                        className="btn btn-sm"
                                                        onClick={() => openDetail(h.id)}
                                                    >
                                                        Ver detalle
                                                    </button>

                                                    {String(h.status).toLowerCase() !== "entregado" && (
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => markAsDelivered(h.id)}
                                                        >
                                                            Marcar entregado
                                                        </button>
                                                    )}
                                                </div>
                                            </td>

                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* modal detalle */}
            {detailOpenId && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-base-100 rounded-lg p-0 max-w-6xl w-[96%] max-h-[90vh] flex flex-col relative">
                        {/* header sticky */}
                        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-base-100 z-10 rounded-t-lg">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-lg font-semibold">Detalle de pedido</h3>

                                {detailDoc && (
                                    <span
                                        className={
                                            "badge " + statusBadgeClass(detailDoc.status)
                                        }
                                    >
                                        {statusToEs(detailDoc.status)}
                                    </span>
                                )}

                                {detailDoc &&
                                    String(detailDoc.status).toLowerCase() !== "entregado" && (
                                        <button
                                            className="btn btn-sm btn-success"
                                            onClick={() =>
                                                markAsDelivered(detailDoc.id, {
                                                    alsoUpdateDetail: true,
                                                })
                                            }
                                        >
                                            Marcar entregado
                                        </button>
                                    )}
                            </div>

                            <button
                                className="btn btn-sm btn-ghost"
                                onClick={closeDetail}
                            >
                                Cerrar
                            </button>
                        </div>


                        {/* scrollable body */}
                        <div className="px-5 py-4 overflow-y-auto">
                            {!detailDoc && <p>Cargando…</p>}

                            {detailDoc && (
                                <>
                                    <div className="mb-3 text-sm opacity-80">
                                        <div>
                                            <b>Inicio:</b> {fmtDateTime(detailDoc.createdAt)}{" "}
                                            {detailDoc.finishedAt && (
                                                <>
                                                    {" "}
                                                    · <b>Fin:</b>{" "}
                                                    {fmtDateTime(detailDoc.finishedAt)}
                                                </>
                                            )}
                                        </div>
                                        <div>
                                            <b>Estado:</b>{" "}
                                            {statusToEs(detailDoc.status)}
                                        </div>
                                        <div>
                                            <b>Período:</b>{" "}
                                            {fmtYMD(detailDoc?.period?.start)} →{" "}
                                            {fmtYMD(detailDoc?.period?.end)}
                                        </div>
                                        <div>
                                            <b>Resumen:</b>{" "}
                                            {[
                                                `Emails: ${safeNum(
                                                    detailDoc?.totals?.emailsCount
                                                ) || 0
                                                }`,
                                                `Labs: ${detailDoc?.totals?.labsCount > 0
                                                    ? detailDoc.totals.labsCount
                                                    : computeLabsCountFallback(
                                                        detailDoc
                                                    )
                                                }`,
                                                // `Productos: ${
                                                //     safeNum(
                                                //         detailDoc?.totals
                                                //             ?.productosCount
                                                //     ) || 0
                                                // }`,
                                                // `Unidades: ${
                                                //     safeNum(
                                                //         detailDoc?.totals
                                                //             ?.unidadesTotal
                                                //     ) || 0
                                                // }`,
                                            ].join(" · ")}
                                        </div>
                                    </div>


<div className="mb-4">
  <div className="font-semibold mb-2">Resultado por laboratorio y sucursal</div>

  {Array.isArray(detailDoc?.details?.outcomes) && detailDoc.details.outcomes.length ? (
    <div className="overflow-x-auto">
      <table className="table table-compact w-full">
        <thead>
          <tr>
            <th>Laboratorio</th>
            <th>Sucursal</th>
            <th>Estado</th>
            <th>Detalle</th>
          </tr>
        </thead>
        <tbody>
          {detailDoc.details.outcomes.map((o, idx) => {
            const st = String(o?.status || "");
            const badge =
              st === "sent" ? "badge-success" :
              st === "skipped" ? "badge-ghost" :
              st === "error" ? "badge-error" :
              "badge-ghost";

            const label =
              st === "sent" ? "Enviado" :
              st === "skipped" ? "Sin productos" :
              st === "error" ? "Error" :
              "-";

            const items = safeNum(o?.totals?.items);
            const un = safeNum(o?.totals?.unidades);

            const detailText =
              st === "sent"
                ? `Ítems: ${items} · Unidades: ${un}`
                : (o?.reason || "-");

            return (
              <tr key={idx}>
                <td className="font-medium">{o?.labDisplay || o?.labNorm || "-"}</td>
                <td>{o?.destSucName || `Suc ${o?.destSucId || "-"}`}</td>
                <td><span className={"badge " + badge}>{label}</span></td>
                <td className="text-xs opacity-80">{detailText}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="text-sm opacity-70">
      Este pedido no tiene outcomes guardados (versión anterior del backend).
    </div>
  )}
</div>



                                    {/* Bloques por email */}
                                    {(() => {
                                        const emails = extractEmails(detailDoc);
                                        if (!emails.length) {
                                            return (
                                                <p className="opacity-70">
                                                    No hay detalle de emails
                                                    para este pedido.
                                                </p>
                                            );
                                        }

                                        return (
                                            <div className="flex flex-col gap-4">
                                                {emails.map((em, i) => {
                                                    const productos = safeArray(
                                                        em?.productos
                                                    )
                                                        .slice()
                                                        .sort((a, b) =>
                                                            safeStr(
                                                                a?.descripcion
                                                            ).localeCompare(
                                                                safeStr(
                                                                    b?.descripcion
                                                                ),
                                                                "es",
                                                                {
                                                                    sensitivity:
                                                                        "base",
                                                                    numeric: true,
                                                                }
                                                            )
                                                        );
                                                    const unidades = safeNum(
                                                        em?.totals?.unidades
                                                    );
                                                    const items = safeNum(
                                                        em?.totals?.items
                                                    );
                                                    return (
                                                        <div
                                                            key={i}
                                                            className="rounded border p-3 bg-base-200/30"
                                                        >
                                                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                                                <div>
                                                                    <div className="font-semibold">
                                                                        {safeStr(
                                                                            em?.ownerSucName
                                                                        ) ||
                                                                            "Sucursal"}{" "}
                                                                        —{" "}
                                                                        {safeStr(
                                                                            em?.labDisplay
                                                                        ) ||
                                                                            safeStr(
                                                                                em?.labNorm
                                                                            ) ||
                                                                            "Lab"}
                                                                    </div>
                                                                    <div className="text-sm opacity-80">
                                                                        <b>
                                                                            Asunto:
                                                                        </b>{" "}
                                                                        {safeStr(
                                                                            em?.subject
                                                                        ) || "-"}
                                                                        {em?.attachmentName && (
                                                                            <>
                                                                                {" "}
                                                                                ·{" "}
                                                                                <b>
                                                                                    Adjunto:
                                                                                </b>{" "}
                                                                                {safeStr(
                                                                                    em?.attachmentName
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-sm opacity-80">
                                                                        <b>
                                                                            Destinatarios:
                                                                        </b>{" "}
                                                                        {safeArray(
                                                                            em?.to
                                                                        ).join(
                                                                            ", "
                                                                        ) || "-"}
                                                                    </div>
                                                                </div>
                                                                <div className="text-sm">
                                                                    <span className="badge mr-2">
                                                                        Ítems:{" "}
                                                                        {items}
                                                                    </span>
                                                                    <span className="badge">
                                                                        Unidades:{" "}
                                                                        {
                                                                            unidades
                                                                        }
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="overflow-x-auto">
                                                                <table className="table table-compact w-full">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>
                                                                                #
                                                                            </th>
                                                                            <th>
                                                                                Descripción
                                                                            </th>
                                                                            <th>
                                                                                SKU/Key
                                                                            </th>
                                                                            <th>
                                                                                CB (código de barras)
                                                                            </th>
                                                                            <th>
                                                                                Pedido
                                                                            </th>
                                                                            <th>
                                                                                Distribución
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {productos.map(
                                                                            (
                                                                                p,
                                                                                idx
                                                                            ) => (
                                                                                <tr
                                                                                    key={
                                                                                        idx
                                                                                    }
                                                                                >
                                                                                    <td>
                                                                                        {idx +
                                                                                            1}
                                                                                    </td>
                                                                                    <td>
                                                                                        {safeStr(
                                                                                            p?.descripcion
                                                                                        ) ||
                                                                                            "-"}
                                                                                    </td>
                                                                                    <td>
                                                                                        {safeStr(
                                                                                            p?.productKey
                                                                                        ) ||
                                                                                            "-"}
                                                                                    </td>
                                                                                    <td>
                                                                                        {p?.cb ? String(p.cb) : "-"}
                                                                                    </td>
                                                                                    <td>
                                                                                        {safeNum(
                                                                                            p?.cantidad
                                                                                        ) ||
                                                                                            0}
                                                                                    </td>
                                                                                    <td className="text-xs opacity-80">
                                                                                        {Object.entries(
                                                                                            p?.distPorSuc ||
                                                                                            {}
                                                                                        )
                                                                                            .map(
                                                                                                ([
                                                                                                    sid,
                                                                                                    val,
                                                                                                ]) =>
                                                                                                    `${sid}: ${safeNum(
                                                                                                        val
                                                                                                    )}`
                                                                                            )
                                                                                            .join(
                                                                                                " · "
                                                                                            ) ||
                                                                                            "-"}
                                                                                    </td>
                                                                                </tr>
                                                                            )
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
